import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callbacksApi, clientsApi, gatewaysApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { Mono, CodePill, StatusDot } from "@/components/Atoms";
import { Plus, Trash2, Pencil, Send, Search, X, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import CodeEditor from "@/components/CodeEditor";
import { useCan } from "@/lib/rbac";

// Well-known flag values the backend emits for BLE advertising / LoRa packet
// types. Users can still type their own custom values via the chip input.
const COMMON_FLAGS = ["0x01", "0x02", "0x04", "0x08", "0x10", "0x20"];

const DEFAULT_TEST_PAYLOAD = {
  client_id: "<client-id>",
  device_mac: "AA:BB:CC:DD:EE:FF",
  gw_mac: "11:22:33:44:55:66",
  gw_hw: "minew_g1",
  flag: "0x01",
  received_at: new Date().toISOString(),
  payload_hex: "01020304aabbccdd",
  parsed: {
    rssi: -64,
    battery: 87,
    temperature: 21.4,
  },
};

function HeadersEditor({ headers, onChange }) {
  const rows = Object.entries(headers || {});
  const update = (newRows) => onChange(Object.fromEntries(newRows.filter(([k]) => k.trim())));
  return (
    <div className="space-y-2">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex gap-2">
          <input className="flex-1 border border-zinc-200 rounded-md px-2.5 py-1.5 text-xs font-mono" value={k}
            onChange={(e) => { const r = [...rows]; r[i] = [e.target.value, v]; update(r); }} placeholder="header-name" />
          <input className="flex-1 border border-zinc-200 rounded-md px-2.5 py-1.5 text-xs font-mono" value={v}
            onChange={(e) => { const r = [...rows]; r[i] = [k, e.target.value]; update(r); }} placeholder="value" />
          <button type="button" onClick={() => update(rows.filter((_, j) => j !== i))} className="px-2 text-zinc-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => update([...rows, ["", ""]])} className="text-xs text-zinc-600 hover:text-zinc-900">+ add header</button>
    </div>
  );
}

// ChipInput — free-form multi-value input rendered as removable chips.
// Users type a value, press Enter (or comma), and it's appended.
// Optional `suggestions` render as a picker row below the input.
function ChipInput({ label, values, suggestions = [], onChange, placeholder, validate, testid }) {
  const [draft, setDraft] = useState("");
  const add = (raw) => {
    const v = (raw ?? draft).trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(""); return; }
    if (validate && !validate(v)) {
      toast.error(`"${v}" is not a valid ${label.toLowerCase()}`);
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };
  const remove = (i) => onChange(values.filter((_, j) => j !== i));
  const availableSuggestions = suggestions.filter((s) => !values.includes(s));
  return (
    <div>
      <div className="text-xs text-zinc-600">{label}</div>
      <div
        data-testid={testid}
        className="mt-1 min-h-[38px] flex flex-wrap gap-1.5 items-center border border-zinc-200 rounded-md px-2 py-1.5 bg-white focus-within:ring-1 focus-within:ring-[#3730A3] focus-within:border-[#3730A3]"
      >
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            data-testid={`${testid}-chip-${v}`}
            className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-800 text-xs font-mono rounded px-2 py-0.5"
          >
            {v}
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-zinc-500 hover:text-red-600"
              aria-label={`Remove ${v}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => {
            const v = e.target.value;
            // Allow the shell to still paste "0x01, 0x02" — split on comma.
            if (v.includes(",")) {
              v.split(",").map((x) => x.trim()).filter(Boolean).forEach((x) => add(x));
              setDraft("");
            } else {
              setDraft(v);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(); }
            else if (e.key === "Backspace" && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => add()}
          placeholder={values.length ? "" : placeholder}
          className="flex-1 min-w-[80px] text-xs font-mono outline-none bg-transparent"
        />
      </div>
      {availableSuggestions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono self-center">common:</span>
          {availableSuggestions.slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="text-[10px] font-mono border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600 hover:bg-zinc-50"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// FilterBuilder — visual editor for a callback's filter_json. Turns the
// three fixed keys (flags, gw_hw, boolean flags) into a proper composable UI
// with chip inputs, multi-select of known gateway hardware, and a live JSON
// preview so users can see the exact payload they're about to save.
function FilterBuilder({ value, onChange }) {
  const val = value || {};
  const flags = val.flags || [];
  const gwHw = val.gw_hw || [];

  const hwQuery = useQuery({ queryKey: ["gw-hw"], queryFn: gatewaysApi.hwList });
  const knownHw = useMemo(
    () => (hwQuery.data || []).map((h) => h.canonical_hw).filter(Boolean).sort(),
    [hwQuery.data]
  );

  const set = (patch) => onChange({ ...val, ...patch });
  const isValidFlag = (s) => /^0x[0-9a-fA-F]+$/.test(s);

  // A canonical, sorted JSON string that omits empty arrays/false booleans
  // so users don't see distracting noise in the preview.
  const compact = useMemo(() => {
    const out = {};
    if (flags.length) out.flags = flags;
    if (gwHw.length) out.gw_hw = gwHw;
    if (val.has_fix) out.has_fix = true;
    if (val.has_status) out.has_status = true;
    return out;
  }, [flags, gwHw, val.has_fix, val.has_status]);

  const hasAny = flags.length || gwHw.length || val.has_fix || val.has_status;

  return (
    <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/50 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Filter</div>
        {hasAny && (
          <button
            type="button"
            onClick={() => onChange({ flags: [], gw_hw: [], has_fix: false, has_status: false })}
            data-testid="filter-clear"
            className="text-[10px] text-zinc-500 hover:text-red-600 font-mono uppercase tracking-widest"
          >
            clear all
          </button>
        )}
      </div>

      <ChipInput
        testid="filter-flags"
        label="Flags"
        values={flags}
        suggestions={COMMON_FLAGS}
        placeholder="type a flag (e.g. 0x01) and press Enter"
        validate={isValidFlag}
        onChange={(next) => set({ flags: next })}
      />

      <div>
        <div className="text-xs text-zinc-600">Gateway hardware</div>
        {hwQuery.isLoading ? (
          <div className="mt-1 text-[11px] text-zinc-400 font-mono">Loading hardware list…</div>
        ) : knownHw.length > 0 ? (
          <div
            data-testid="filter-gw-hw"
            className="mt-1 flex flex-wrap gap-1.5 border border-zinc-200 rounded-md bg-white p-2 max-h-32 overflow-y-auto"
          >
            {knownHw.map((hw) => {
              const on = gwHw.includes(hw);
              return (
                <button
                  key={hw}
                  type="button"
                  onClick={() =>
                    set({ gw_hw: on ? gwHw.filter((h) => h !== hw) : [...gwHw, hw] })
                  }
                  data-testid={`filter-gw-hw-${hw}`}
                  className={`text-xs font-mono rounded px-2 py-0.5 border transition ${
                    on
                      ? "bg-[#3730A3] text-white border-[#3730A3]"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  {on ? <Check className="w-3 h-3 inline mr-1" /> : null}
                  {hw}
                </button>
              );
            })}
          </div>
        ) : (
          // Fallback when no HW is registered yet — still let users type manually.
          <ChipInput
            testid="filter-gw-hw-manual"
            label=""
            values={gwHw}
            onChange={(next) => set({ gw_hw: next })}
            placeholder="type a canonical_hw and press Enter"
          />
        )}
        <div className="mt-1 text-[10px] text-zinc-400 font-mono">
          {gwHw.length ? `${gwHw.length} selected` : "match any hardware"}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center gap-2 text-xs">
          <input
            data-testid="filter-has-fix"
            type="checkbox"
            checked={!!val.has_fix}
            onChange={(e) => set({ has_fix: e.target.checked })}
          />
          Require GPS fix
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input
            data-testid="filter-has-status"
            type="checkbox"
            checked={!!val.has_status}
            onChange={(e) => set({ has_status: e.target.checked })}
          />
          Require status block
        </label>
      </div>

      <details className="border-t border-zinc-200 pt-2">
        <summary className="cursor-pointer flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-500 font-mono hover:text-zinc-800">
          <ChevronDown className="w-3 h-3" />
          Preview JSON payload
        </summary>
        <pre
          data-testid="filter-preview"
          className="mt-2 text-[11px] font-mono bg-zinc-900 text-zinc-100 rounded p-2 overflow-x-auto"
        >
          {hasAny ? JSON.stringify(compact, null, 2) : "{ /* matches everything */ }"}
        </pre>
        <div className="text-[10px] text-zinc-400 mt-1">
          Only non-empty keys are sent — the backend treats missing keys as
          &quot;no restriction&quot;.
        </div>
      </details>
    </div>
  );
}

function CallbackForm({ initial, clients, onSubmit, onCancel }) {
  const [form, setForm] = useState(
    initial || {
      client_id: clients[0]?.id || "",
      url: "",
      method: "POST",
      headers: {},
      source: "device",
      filter_json: { flags: [], gw_hw: [], has_fix: false, has_status: false },
      enabled: true,
    }
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.url.trim()) { toast.error("URL is required"); return; }
        onSubmit(form);
      }}
      className="space-y-3 max-h-[70vh] overflow-y-auto pr-1"
    >
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Client</span>
          <select className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Method</span>
          <select className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
            {["POST", "PUT", "PATCH"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">URL</span>
        <input data-testid="callback-input-url" className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono outline-none focus:ring-1 focus:ring-[#3730A3]" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://example.com/webhook" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Source</span>
        <select className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
          <option value="device">device</option>
          <option value="gateway_auto">gateway_auto</option>
        </select>
      </label>

      <div>
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Headers</span>
        <div className="mt-1.5">
          <HeadersEditor headers={form.headers} onChange={(h) => setForm({ ...form, headers: h })} />
        </div>
      </div>

      <FilterBuilder
        value={form.filter_json}
        onChange={(next) => setForm({ ...form, filter_json: next })}
      />

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
        Enabled
      </label>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">Cancel</button>
        <button data-testid="callback-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900">Save</button>
      </div>
    </form>
  );
}

function CallbackTestModal({ callback, onClose }) {
  const [bodyText, setBodyText] = useState(JSON.stringify(DEFAULT_TEST_PAYLOAD, null, 2));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    let payload;
    try {
      payload = JSON.parse(bodyText);
    } catch (e) {
      toast.error("Body is not valid JSON: " + e.message);
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const r = await callbacksApi.test({ url: callback.url, method: callback.method, headers: callback.headers, payload });
      setResult(r);
      if (r.ok || r.status < 400) toast.success(`${callback.method} ${callback.url} → ${r.status} in ${r.response_ms}ms`);
      else toast.error(`Test failed: ${r.status}`);
    } catch (e) {
      toast.error(e.message);
      setResult({ ok: false, error: e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-3xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold mb-1">Test callback</h2>
        <p className="text-xs text-zinc-500 font-mono mb-3">{callback.method} {callback.url}</p>

        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Body (JSON)</div>
        <CodeEditor value={bodyText} onChange={setBodyText} language="json" height="280px" />

        <div className="flex items-center gap-2 mt-3">
          <button
            data-testid="callback-test-reset"
            type="button"
            onClick={() => setBodyText(JSON.stringify(DEFAULT_TEST_PAYLOAD, null, 2))}
            className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50"
          >Reset to default</button>
          <div className="flex-1" />
          <button type="button" onClick={onClose} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">Close</button>
          <button
            data-testid="callback-test-run"
            type="button"
            disabled={running}
            onClick={run}
            className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Send className="w-3.5 h-3.5" /> {running ? "Sending…" : "Send test"}
          </button>
        </div>

        {result && (
          <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="callback-test-result">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Response</div>
            <Mono className="text-xs">
              {result.error
                ? `error · ${result.error}`
                : `status ${result.status} · ${result.response_ms}ms`}
            </Mono>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Callbacks() {
  const qc = useQueryClient();
  const canWrite = useCan("write:callbacks");
  const canTest = useCan("test:callbacks");
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "enabled" | "disabled"
  const [modal, setModal] = useState(null);
  const [testModal, setTestModal] = useState(null);

  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const list = useQuery({ queryKey: ["callbacks", clientFilter], queryFn: () => callbacksApi.list(clientFilter ? { client_id: clientFilter } : {}) });
  const inval = () => qc.invalidateQueries({ queryKey: ["callbacks"] });
  const cM = useMutation({ mutationFn: callbacksApi.create, onSuccess: () => { toast.success("Callback created"); inval(); setModal(null); } });
  const uM = useMutation({ mutationFn: ({ id, data }) => callbacksApi.update(id, data), onSuccess: () => { toast.success("Callback updated"); inval(); setModal(null); } });
  const dM = useMutation({ mutationFn: callbacksApi.remove, onSuccess: () => { toast.success("Callback deleted"); inval(); } });

  const clientName = (id) => clients.data?.find((c) => c.id === id)?.name || id || "—";

  const filtered = (list.data || []).filter((c) => {
    if (search) {
      const s = search.toLowerCase();
      const matches =
        (c.url || "").toLowerCase().includes(s) ||
        clientName(c.client_id).toLowerCase().includes(s);
      if (!matches) return false;
    }
    if (sourceFilter && c.source !== sourceFilter) return false;
    if (statusFilter === "enabled" && !c.enabled) return false;
    if (statusFilter === "disabled" && c.enabled) return false;
    return true;
  });
  const pager = usePager(filtered, { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <PageContainer>
      <PageHeader
        title="Callbacks"
        subtitle="Per-client webhook destinations with JSON filtering."
        actions={
          canWrite ? (
            <button
              data-testid="callback-new"
              onClick={() => setModal({ mode: "create" })}
              className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> New callback
            </button>
          ) : null
        }
      />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
            <input
              data-testid="callback-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search URL or client…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
            />
          </div>
          <select
            data-testid="callback-filter-client"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All clients</option>
            {clients.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            data-testid="callback-filter-source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All sources</option>
            <option value="device">device</option>
            <option value="gateway_auto">gateway_auto</option>
          </select>
          <select
            data-testid="callback-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All statuses</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>

        <div className="px-4 py-2.5 border-b border-zinc-200 text-xs text-zinc-500 font-mono">
          {filtered.length} of {list.data?.length || 0} callbacks
        </div>

        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-3 font-semibold">Client</th>
              <th className="text-left p-3 font-semibold">URL</th>
              <th className="text-left p-3 font-semibold">Method</th>
              <th className="text-left p-3 font-semibold">Source</th>
              <th className="text-left p-3 font-semibold">Filter</th>
              <th className="text-left p-3 font-semibold">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pager.pageRows.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100" data-testid={`callback-row-${c.id}`}>
                <td className="p-3 font-medium text-zinc-800">{clientName(c.client_id)}</td>
                <td className="p-3"><Mono>{c.url}</Mono></td>
                <td className="p-3"><CodePill>{c.method}</CodePill></td>
                <td className="p-3"><CodePill>{c.source}</CodePill></td>
                <td className="p-3"><Mono className="text-zinc-600 truncate block max-w-[280px]">{JSON.stringify(c.filter_json)}</Mono></td>
                <td className="p-3"><StatusDot online={c.enabled} label={c.enabled ? "enabled" : "paused"} /></td>
                <td className="p-3 text-right">
                  {canTest && (
                    <button data-testid={`callback-test-${c.id}`} onClick={() => setTestModal(c)} className="p-1.5 rounded hover:bg-zinc-100" title="Test"><Send className="w-4 h-4" /></button>
                  )}
                  {canWrite && (
                    <>
                      <button onClick={() => setModal({ mode: "edit", row: c })} className="p-1.5 rounded hover:bg-zinc-100"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`callback-delete-${c.id}`} onClick={() => { if (window.confirm("Delete callback?")) dM.mutate(c.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {pager.total === 0 && <tr><td colSpan={7} className="py-8 text-center text-sm text-zinc-500">No callbacks match the filters.</td></tr>}
          </tbody>
        </table>
        <Pager {...pager} testidPrefix="callback-pager" />
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">{modal.mode === "create" ? "New callback" : "Edit callback"}</h2>
            <CallbackForm
              initial={modal.row}
              clients={clients.data || []}
              onCancel={() => setModal(null)}
              onSubmit={(form) => modal.mode === "create" ? cM.mutate(form) : uM.mutate({ id: modal.row.id, data: form })}
            />
          </div>
        </div>
      )}

      {testModal && (
        <CallbackTestModal callback={testModal} onClose={() => setTestModal(null)} />
      )}
    </PageContainer>
  );
}
